import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { GitCompareArrows, X } from "lucide-react";
import { useCompare, COMPARE_MAX } from "@/lib/compare";
import { useLang } from "@/lib/i18n";
import { api, imgSrc } from "@/lib/api";
import type { Yacht } from "@/types";

export default function CompareBar() {
  const { ids, remove, clear } = useCompare();
  const { t } = useLang();
  const { pathname } = useLocation();
  const { data = [] } = useQuery<Yacht[]>({ queryKey: ["yachts"], queryFn: async () => (await api.get("/yachts")).data, enabled: ids.length > 0 });
  if (ids.length === 0 || pathname === "/comparar") return null;
  const sel = ids.map((id) => data.find((y) => y.id === id)).filter(Boolean) as Yacht[];
  return (
    <div data-testid="compare-bar" className="fixed bottom-0 inset-x-0 z-40 px-3 pb-3 sm:px-6 sm:pb-5 pointer-events-none">
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl bg-[#0B192C]/95 backdrop-blur-md border border-white/10 text-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] px-4 py-3 sm:px-5 flex items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {sel.map((y) => (
            <div key={y.id} className="relative shrink-0" data-testid={`compare-bar-item-${y.id}`}>
              <img src={imgSrc(y.photos[0])} alt={y.name} className="h-11 w-14 sm:h-12 sm:w-16 rounded-lg object-cover border border-white/20" />
              <button onClick={() => remove(y.id)} aria-label="x" data-testid={`compare-bar-remove-${y.id}`} className="absolute -top-1.5 -right-1.5 grid h-5 w-5 place-items-center rounded-full bg-white text-[#0B192C]"><X className="h-3 w-3" /></button>
            </div>
          ))}
          {Array.from({ length: COMPARE_MAX - sel.length }).map((_, i) => <div key={i} className="h-11 w-14 sm:h-12 sm:w-16 shrink-0 rounded-lg border border-dashed border-white/25" />)}
          <p className="hidden sm:block ml-2 text-xs text-white/60 truncate">{t("cmp.selected", { n: sel.length, max: COMPARE_MAX })}</p>
        </div>
        <button onClick={clear} data-testid="compare-bar-clear" className="text-xs text-white/60 hover:text-white">{t("cmp.clear")}</button>
        <Link to="/comparar" data-testid="compare-bar-go" aria-disabled={sel.length < 2}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs sm:text-sm font-semibold transition-colors ${sel.length >= 2 ? "bg-[#D4AF37] text-[#0B192C] hover:bg-[#F3E5AB]" : "bg-white/10 text-white/40 pointer-events-none"}`}>
          <GitCompareArrows className="h-4 w-4" />{t("cmp.go")}
        </Link>
      </div>
    </div>
  );
}
